import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
} from "@mui/material";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarExport,
} from "@mui/x-data-grid";
import API from "../../api/axiosConfig";
import AccessDeniedPage from "../RedirectPages/AccessDeniedPage";
import { useUser } from "../../context/UserContext";
import { Link as RouterLink } from "react-router-dom";

const InsuranceDashboard = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isAdmin } = useUser();

  // Year Selection State ('CURRENT' or specific archived year integer)
  const [selectedYear, setSelectedYear] = useState("CURRENT");
  const [availableYears, setAvailableYears] = useState([]);

  const planTypeLabels = {
    OPT_OUT: "Opted out",
    BRONZE: "Bronze",
    SILVER: "Silver",
    PLATINUM: "Platinum",
    NONE: "No Plan",
  };

  function CustomToolbar() {
    return (
      <GridToolbarContainer
        sx={{ p: 1, backgroundColor: "background.default" }}
      >
        <GridToolbarExport printOptions={{ disableToolbarButton: true }} />
      </GridToolbarContainer>
    );
  }

  // Fetch distinct list of archived years on mount
  useEffect(() => {
    const fetchArchivedYears = async () => {
      try {
        const response = await API.post("/api/insurance/history/years");
        setAvailableYears(response.data.availableYears || []);
      } catch (err) {
        console.error("Failed to load archived years:", err);
      }
    };

    if (isAdmin) {
      fetchArchivedYears();
    }
  }, [isAdmin]);

  // Fetch insurance data based on current vs archived selection
  const fetchInsuranceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
  
      const response =
        selectedYear === "CURRENT"
          ? await API.post("/api/insurance/dashboard")
          : await API.get(
              `/api/insurance/history/dashboard?year=${selectedYear}`,
            );

      const mappedData = (response.data?.insuranceInfo || []).map((item) => ({
        ...item,
        id: item.employeeId,
      }));

      setRows(mappedData);
    } catch (err) {
      setError("Failed to load employee insurance information.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => {
    if (isAdmin) {
      fetchInsuranceData();
    }
  }, [fetchInsuranceData, isAdmin]);

  const columns = [
    {
      field: "employeeId",
      headerName: "ID",
      width: 90,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "employeeName",
      headerName: "Employee Name",
      flex: 1,
      minWidth: 180,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const targetId = params.row.employeeId;

        return (
          <Link
            component={RouterLink}
            to={`/admin?highlight=${targetId}`}
            underline="hover"
            sx={{
              fontWeight: "bold",
              color: "inherit",
              cursor: "pointer",
              "&:hover": {
                color: "primary.dark",
              },
            }}
          >
            {params.value}
          </Link>
        );
      },
    },
    {
      field: "active",
      headerName: "Employment Status",
      width: 160,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Chip
          label={params.value ? "Active" : "Inactive"}
          color={params.value ? "success" : "default"}
          size="small"
          variant="outlined"
        />
      ),
    },
    {
      field: "planType",
      headerName: "Insurance Plan",
      width: 160,
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) => {
        if (!value) return "No Plan";
        return planTypeLabels[value] || value;
      },
    },
    {
      field: "companyHealthCost",
      headerName: "Company Health Cost",
      width: 150,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (value, row) => {
        const company = Number(row.companyCost) || 0;
        const dental = Number(row.dentalCost) || 0;
        const vision = Number(row.visionCost) || 0;

        return company - vision - dental;
      },
      valueFormatter: (value) =>
        value != null ? `$${Number(value).toFixed(2)}` : "$0.00",
    },
    {
      field: "visionCost",
      headerName: "Vision Cost",
      width: 150,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) =>
        value != null ? `$${Number(value).toFixed(2)}` : "$0.00",
    },
    {
      field: "dentalCost",
      headerName: "Dental Cost",
      width: 150,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) =>
        value != null ? `$${Number(value).toFixed(2)}` : "$0.00",
    },
    {
      field: "companyCost",
      headerName: "Total Company Cost",
      width: 150,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) =>
        value != null ? `$${Number(value).toFixed(2)}` : "$0.00",
    },
    {
      field: "employeeCost",
      headerName: "Employee Cost",
      width: 150,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueGetter: (value, row) => {
        const total = Number(row.totalPlanCost) || 0;
        const company = Number(row.companyCost) || 0;

        return total - company;
      },
      valueFormatter: (value) =>
        value != null ? `$${Number(value).toFixed(2)}` : "$0.00",
    },
    {
      field: "totalPlanCost",
      headerName: "Total Cost",
      width: 150,
      type: "number",
      align: "center",
      headerAlign: "center",
      valueFormatter: (value) =>
        value != null ? `$${Number(value).toFixed(2)}` : "$0.00",
    },
  ];

  const aggregateTotals = rows.reduce(
    (acc, row) => {
      const total = Number(row.totalPlanCost) || 0;
      const companyTotal = Number(row.companyCost) || 0;

      acc.companyTotal += companyTotal;
      acc.employeeTotal += total - companyTotal;
      acc.total += total;
      return acc;
    },
    { companyTotal: 0, employeeTotal: 0, total: 0 },
  );

  if (!isAdmin) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "60vh",
        }}
      >
        <AccessDeniedPage />
      </Box>
    );
  }

  return (
    <Box
      sx={{ p: 4, width: "100%", maxWidth: "1200px", margin: "0 auto", pb: 10 }}
    >
      {/* Header Row with Plan Year Filter */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4" sx={{ fontWeight: "bold", mb: 0.5 }}>
            Employee Insurance Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedYear === "CURRENT"
              ? "Overview of active employee insurance information and costs."
              : `Historical snapshot for plan year ${selectedYear}.`}
          </Typography>
        </Box>

        {/* Select active year vs. archived history year */}
        <FormControl size="small" sx={{ minWidth: 200, mt: { xs: 2, sm: 0 } }}>
          <InputLabel id="year-select-label">Plan Year</InputLabel>
          <Select
            labelId="year-select-label"
            value={selectedYear}
            label="Plan Year"
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            <MenuItem value="CURRENT">Active Enrollment </MenuItem>
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                Archived: {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Grid Canvas Wrapper */}
      <Paper
        elevation={2}
        sx={{ width: "100%", borderRadius: 2, overflow: "hidden" }}
      >
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              p: 6,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            hideFooterPagination={true}
            autoHeight={true}
            disableRowSelectionOnClick
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              toolbar: {
                sx: { backgroundColor: "background.default" },
              },
            }}
            sx={{
              border: "none",
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "background.default",
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid",
                borderColor: "divider",
              },
            }}
          />
        )}

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "center", sm: "flex-end" },
            flexWrap: "wrap",
            mt: 3,
            gap: 2,
            p: 2,
          }}
        >
          <Paper
            variant="outlined"
            sx={{ p: 2, textAlign: "center", minWidth: 160, borderRadius: 2 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
            >
              Total Company Cost
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold", mt: 0.5 }}>
              $
              {Number(aggregateTotals.companyTotal).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Paper>

          <Paper
            variant="outlined"
            sx={{ p: 2, textAlign: "center", minWidth: 160, borderRadius: 2 }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
            >
              Total Employee Cost
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold", mt: 0.5 }}>
              $
              {Number(aggregateTotals.employeeTotal).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: 2,
              textAlign: "center",
              minWidth: 160,
              borderRadius: 2,
              bgcolor: "action.hover",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
            >
              Overall Cost
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: "bold", mt: 0.5, color: "success.main" }}
            >
              $
              {Number(aggregateTotals.total).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </Typography>
          </Paper>
        </Box> 
      </Paper>
    </Box>
  );
};

export default InsuranceDashboard;
